// Update the Login.js file with this new code
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle splash screen animation
  useEffect(() => {
    setTimeout(() => {
      setShowSplash(false);
    }, 600); // 1.5 seconds for the splash screen
  }, []);

  // Get the page user was trying to access or default routes based on role
  const getRedirectPath = (userRole) => {
    // Check if there was a requested page before login
    const intended = location.state?.from?.pathname;
    if (intended && intended !== '/login') {
      return intended;
    }

    // Default routes based on role
    switch (userRole) {
      case 'wholesale':
        return '/wholesale/catalog';
      case 'admin':
        return '/admin/dashboard';
      case 'user':
      default:
        return '/dashboard';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      // Get appropriate redirect path based on user role
      const redirectPath = getRedirectPath(userData.user.role);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {showSplash ? (
        <div className="splash-screen">
          <svg className="splash-logo" viewBox="0 0 15.71 13.9">
            <path fill="#7DCAB1" d="M7.86,2.19c.21,0,.42,.1,.54,.31l5.03,8.24c.26,.43-.05,.97-.54,.97h-4.24c-.5,0-.8-.54-.55-.97,.42-.69,.79-1.32,.84-1.39,.11-.18,.05-.49-.05-.66-.11-.16-.04-.34-.04-.34,0,0,.56-1.08,.59-1.08,0,0,0,0,0,0,0,0,0,0,.01,0,.01,0,.02-.01,.02-.01,0,0,.05-.09,.07-.11,.01-.02,0-.04,0-.04,0,0,.01-.02,.04-.06,.03-.04-.06-.1-.06-.1l-.15-.09h0s-.08-.05-.08-.05l-.32-.17h0s-.15-.09-.15-.09c0,0-.04-.02-.08-.02-.02,0-.03,0-.04,.02-.03,.04-.04,.06-.04,.06,0,0,0,0-.01,0,0,0-.02,0-.03,.01-.01,.02-.07,.11-.07,.11,0,0-.01,.02,0,.04,.02,.01-.77,1.16-.77,1.16,0,0-.11,.13-.29,.13,0,0-.02,0-.03,0-.01,0-.03,0-.04,0-.19,0-.46,.1-.56,.27-.09,.15-1.34,2.27-1.82,3.07-.11,.19-.32,.31-.55,.31h-1.66c-.5,0-.8-.54-.54-.97L7.31,2.5c.12-.2,.33-.31,.54-.31m0-2.19c-.99,0-1.9,.51-2.42,1.36L.41,9.6c-.53,.87-.55,1.97-.05,2.86,.5,.89,1.45,1.44,2.47,1.44h1.66c.79,0,1.54-.34,2.07-.9,.53,.57,1.28,.9,2.07,.9h4.24c1.02,0,1.97-.55,2.47-1.44,.5-.89,.48-1.99-.05-2.86L10.27,1.36c-.52-.85-1.42-1.36-2.42-1.36h0Z"/>
          </svg>
        </div>
      ) : (
        <div className="login-wrapper">
          <div className="login-box">
            <div className="logo-container">
              <svg className="logo" viewBox="0 0 15.71 13.9">
                <path fill="#7DCAB1" d="M7.86,2.19c.21,0,.42,.1,.54,.31l5.03,8.24c.26,.43-.05,.97-.54,.97h-4.24c-.5,0-.8-.54-.55-.97,.42-.69,.79-1.32,.84-1.39,.11-.18,.05-.49-.05-.66-.11-.16-.04-.34-.04-.34,0,0,.56-1.08,.59-1.08,0,0,0,0,0,0,0,0,0,0,.01,0,.01,0,.02-.01,.02-.01,0,0,.05-.09,.07-.11,.01-.02,0-.04,0-.04,0,0,.01-.02,.04-.06,.03-.04-.06-.1-.06-.1l-.15-.09h0s-.08-.05-.08-.05l-.32-.17h0s-.15-.09-.15-.09c0,0-.04-.02-.08-.02-.02,0-.03,0-.04,.02-.03,.04-.04,.06-.04,.06,0,0,0,0-.01,0,0,0-.02,0-.03,.01-.01,.02-.07,.11-.07,.11,0,0-.01,.02,0,.04,.02,.01-.77,1.16-.77,1.16,0,0-.11,.13-.29,.13,0,0-.02,0-.03,0-.01,0-.03,0-.04,0-.19,0-.46,.1-.56,.27-.09,.15-1.34,2.27-1.82,3.07-.11,.19-.32,.31-.55,.31h-1.66c-.5,0-.8-.54-.54-.97L7.31,2.5c.12-.2,.33-.31,.54-.31m0-2.19c-.99,0-1.9,.51-2.42,1.36L.41,9.6c-.53,.87-.55,1.97-.05,2.86,.5,.89,1.45,1.44,2.47,1.44h1.66c.79,0,1.54-.34,2.07-.9,.53,.57,1.28,.9,2.07,.9h4.24c1.02,0,1.97-.55,2.47-1.44,.5-.89,.48-1.99-.05-2.86L10.27,1.36c-.52-.85-1.42-1.36-2.42-1.36h0Z"/>
              </svg>
              <h1 className="brand-name">ALCERA</h1>
            </div>
            
            <h2 className="welcome-text">Welcome back</h2>
            
            {error && (
              <div className="alert alert-danger">{error}</div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                />
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary login-btn" 
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
            
            <div className="login-footer">
              <p className="text-muted">&copy; {new Date().getFullYear()} Alcera Winery Management</p>
            </div>
          </div>
        </div>
      )}
      
      <style jsx="true">{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #121212;
          padding: 20px;
          overflow: hidden;
        }
        
        .splash-screen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #121212;
          z-index: 1000;
          animation: fadeIn 0.3s ease-in, fadeOut 0.3s ease-out 0.5s forwards;
        }
        
        .splash-logo {
          width: 70vmin;  /* 70% of the viewport's smaller dimension */
          height: 70vmin;
          animation: logoGrow 0.5s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; visibility: hidden; }
        }
        
        @keyframes logoGrow {
          0% { transform: scale(0.2); opacity: 0; }
          60% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .login-wrapper {
          width: 100%;
          max-width: 420px;
          opacity: 0;
          animation: loginFadeIn 0.1s ease-in .5s forwards;
        }
        
        @keyframes loginFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .login-box {
          background-color: white;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          padding: 40px;
          text-align: center;
        }
        
        .logo-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 24px;
        }
        
        .logo {
          width: 80px;
          height: 80px;
          margin-bottom: 8px;
        }
        
        .brand-name {
          color: #333;
          font-size: 28px;
          font-weight: 700;
          letter-spacing: 1px;
          margin: 0;
        }
        
        .welcome-text {
          color: #555;
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 24px;
        }
        
        .form-group {
          text-align: left;
          margin-bottom: 20px;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #555;
          font-size: 14px;
        }
        
        .form-control {
          width: 100%;
          padding: 12px 16px;
          font-size: 16px;
          border: 1px solid #ddd;
          border-radius: 8px;
          transition: border-color 0.2s;
        }
        
        .form-control:focus {
          border-color: #7DCAB1;
          box-shadow: 0 0 0 3px rgba(125, 202, 177, 0.25);
          outline: none;
        }
        
        .login-btn {
          background-color: #7DCAB1;
          color: white;
          border: none;
          border-radius: 8px;
          width: 100%;
          padding: 12px;
          font-size: 16px;
          font-weight: 600;
          margin-top: 16px;
          transition: background-color 0.2s;
        }
        
        .login-btn:hover {
          background-color: #6bb89f;
        }
        
        .login-btn:active {
          background-color: #5ca68d;
        }
        
        .login-btn:disabled {
          background-color: #9ad5c1;
          cursor: not-allowed;
        }
        
        .login-footer {
          margin-top: 24px;
          font-size: 12px;
        }
        
        .alert {
          text-align: left;
          margin-bottom: 20px;
          padding: 12px;
          border-radius: 6px;
          font-size: 14px;
        }
        
        .alert-danger {
          background-color: #fff2f2;
          color: #e74c3c;
          border: 1px solid #fad2d2;
        }
        
        @media (max-width: 480px) {
          .login-box {
            padding: 30px 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;